import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';
import {
  useCountryPacks,
  useSetCountryPackEnabled,
  type CountryPack,
} from '@/hooks/use-country-packs';

export function CountryPacksListPage() {
  const t = useT('admin_ui');
  const { data, isLoading } = useCountryPacks();
  const setEnabled = useSetCountryPackEnabled();
  const [error, setError] = useState<string | null>(null);

  const [emptyBefore, emptyAfter] = t(
    'countryPacks.list.empty',
    'No country packs bundled. Add a pack under {path} in the platform repo and rebuild the BOS image.',
  ).split('{path}');

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('countryPacks.list.title', 'Country packs')}</h1>
        <p className="text-sm text-muted-foreground">
          {data === undefined
            ? '…'
            : t(
                'countryPacks.list.summary',
                '{count} pack(s) bundled in this BOS image. Enable a pack to make it available for terminal-profile binding.',
              ).replace('{count}', String(data.length))}
        </p>
      </header>

      <div className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>{t('countryPacks.list.colPack', 'Pack')}</TH>
              <TH>{t('countryPacks.list.colCountry', 'Country')}</TH>
              <TH>{t('countryPacks.list.colVersion', 'Version')}</TH>
              <TH>{t('countryPacks.list.colStatus', 'Status')}</TH>
              <TH>{t('countryPacks.list.colBundled', 'Bundled')}</TH>
              <TH>{t('countryPacks.list.colLastSeen', 'Last seen')}</TH>
              <TH className="w-40" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={7} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.length === 0 && (
              <TR>
                <TD colSpan={7} className="py-6 text-center text-muted-foreground">
                  {emptyBefore}
                  <code>country-packs/&lt;id&gt;/</code>
                  {emptyAfter}
                </TD>
              </TR>
            )}
            {data?.map((p) => (
              <TR key={p.id}>
                <TD className="font-medium">{p.displayName}</TD>
                <TD className="font-mono text-xs">{p.country}</TD>
                <TD className="font-mono text-xs">{p.version}</TD>
                <TD>
                  <EnabledBadge pack={p} />
                </TD>
                <TD>
                  <BundledBadge bundled={p.isBundled} />
                </TD>
                <TD className="text-xs text-muted-foreground">{relative(p.lastSeenAt)}</TD>
                <TD className="space-x-2 text-right">
                  {p.enabled && (
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/country-packs/${p.id}/tax-rates`}>
                        {t('countryPacks.taxRates.title', 'Tax rates')}
                      </Link>
                    </Button>
                  )}
                  {p.enabled ? (
                    // H-61: disable is destructive (orphans terminal-profile
                    // bindings, hides the pack from binding picker). Confirm
                    // before mutating; the binding-count guard on the backend
                    // already rejects when bindings exist, but the UI should
                    // not silently fire the request.
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" disabled={setEnabled.isPending}>
                          {t('countryPacks.list.disable', 'Disable')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t('countryPacks.list.disableTitle', 'Disable {name}?').replace(
                              '{name}',
                              p.displayName,
                            )}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t(
                              'countryPacks.list.disableHelp',
                              'Disabling removes the pack from the terminal-profile binding picker. Any profile currently bound to this pack must be re-bound before its terminals can sync. Bindings on this pack will block the disable; unbind them first.',
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            className={cn(buttonVariants({ variant: 'destructive' }))}
                            onClick={() => {
                              setError(null);
                              setEnabled
                                .mutateAsync({ id: p.id, enabled: false })
                                .catch((err: unknown) => setError(extractError(err)));
                            }}
                          >
                            {t('countryPacks.list.disableConfirm', 'Disable pack')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setEnabled.isPending || !p.isBundled}
                      title={
                        !p.isBundled
                          ? t(
                              'countryPacks.list.missingHint',
                              'Pack is missing from the bundle; cannot enable until re-deployed',
                            )
                          : undefined
                      }
                      onClick={() => {
                        setError(null);
                        setEnabled
                          .mutateAsync({ id: p.id, enabled: true })
                          .catch((err: unknown) => setError(extractError(err)));
                      }}
                    >
                      {t('countryPacks.list.enable', 'Enable')}
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function EnabledBadge({ pack }: { pack: CountryPack }) {
  const t = useT('admin_ui');
  if (pack.enabled)
    return <Badge variant="success">{t('countryPacks.list.badgeEnabled', 'enabled')}</Badge>;
  return <Badge variant="outline">{t('countryPacks.list.badgeDisabled', 'disabled')}</Badge>;
}

function BundledBadge({ bundled }: { bundled: boolean }) {
  const t = useT('admin_ui');
  if (bundled)
    return <Badge variant="outline">{t('countryPacks.list.badgeBundled', 'in this image')}</Badge>;
  return <Badge variant="destructive">{t('countryPacks.list.badgeMissing', 'missing')}</Badge>;
}

function relative(at: string): string {
  const ms = Date.now() - new Date(at).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return 'Unexpected error';
}
